import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PublicUser, UpdateProfileRequest } from '@awwyeah/shared';
import {
  apiLogin,
  apiLogout,
  apiMe,
  apiUpdateProfile,
  type RegisterInput,
  apiRegister,
} from './api';
import { clearLastKnownUser, readLastKnownUser, writeLastKnownUser } from './lastKnownUser';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  user: PublicUser | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  /** `PATCH /api/auth/me`, then updates the in-memory session user. */
  updateProfile: (patch: UpdateProfileRequest) => Promise<PublicUser>;
  /** Re-fetches `/api/auth/me`; useful after external changes to the session. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Session provider for the whole app (ROADMAP §3 WS-2). Wrap the app root with this once
 * — e.g. in `apps/web/src/main.tsx` — so `useUser()` works anywhere below it. WS-2 owns
 * only this module directory; wiring the provider into the root shell is an integration
 * step for whoever assembles the final `main.tsx` (see `docs/DECISIONS.md`).
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const refresh = useCallback(async () => {
    let current: PublicUser | null;
    try {
      current = await apiMe();
    } catch {
      // The server couldn't be reached (offline) or couldn't answer sanely (5xx mid-
      // deploy). The session cookie is still on the device and all feature data lives in
      // IndexedDB, so fall back to the last user this device saw signed in instead of
      // pinning the whole app behind the "Checking session…" screen — this was exactly
      // the airplane bug (Dan, 2026-08-11).
      const remembered = readLastKnownUser(window.localStorage);
      setUser(remembered);
      setStatus(remembered ? 'authenticated' : 'unauthenticated');
      return;
    }
    if (current) {
      writeLastKnownUser(window.localStorage, current);
    }
    // A 401 (`current === null`) deliberately does NOT clear the last-known user: for a
    // household of trusted members, a quietly expired session should cost one online
    // re-login, never the ability to open the app on the next flight (Dan: "robust, not
    // secure", 2026-08-11). Only an explicit logout() clears the fallback.
    setUser(current);
    setStatus(current ? 'authenticated' : 'unauthenticated');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const current = await apiLogin({ email, password });
    writeLastKnownUser(window.localStorage, current);
    setUser(current);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    clearLastKnownUser(window.localStorage);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const current = await apiRegister(input);
    writeLastKnownUser(window.localStorage, current);
    setUser(current);
    setStatus('authenticated');
  }, []);

  const updateProfile = useCallback(async (patch: UpdateProfileRequest) => {
    const updated = await apiUpdateProfile(patch);
    writeLastKnownUser(window.localStorage, updated);
    setUser(updated);
    return updated;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout, register, updateProfile, refresh }),
    [user, status, login, logout, register, updateProfile, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return ctx;
}

/** Convenience hook for the common case: just the current user + loading state. */
export function useUser(): { user: PublicUser | null; status: AuthStatus } {
  const { user, status } = useAuth();
  return { user, status };
}
